/**
 * Riga di un allenamento, uguale nell'elenco e nel giorno scelto del
 * calendario (V10): il foglietto con la data, con la fascia del colore dello
 * stato, ora e luogo, e per gli svolti i presenti sulla rosa.
 */

import { Link } from 'react-router-dom';
import { ClockIcon, MapPinIcon, UsersIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import {
  type AllenamentoInElenco,
  COLORE_SEGNO,
  ETICHETTA_SEGNO,
  type Segno,
  formatoGiornoSettimana,
  formatoMeseBreve,
  formatoNumeroGiorno,
  formatoOra,
} from './allenamenti.js';

export function RigaAllenamento({
  allenamento: a,
  segno,
  ss,
}: {
  allenamento: AllenamentoInElenco;
  segno: Segno;
  ss: string;
}) {
  const data = new Date(a.dataOra);
  const annullato = a.stato === 'annullato';
  return (
    <Link
      to={`/sq/${ss}/allenamenti/${a.id}`}
      className="flex items-center gap-3 rounded-lg border border-border bg-card p-2.5"
    >
      <span
        className={cn(
          'flex w-14 shrink-0 flex-col overflow-hidden rounded-md border border-border bg-background text-center',
          annullato && 'opacity-60',
        )}
      >
        <span className={cn('py-0.5 text-[11px] font-semibold uppercase', COLORE_SEGNO[segno])}>
          {formatoGiornoSettimana.format(data).replace('.', '')}
        </span>
        <span className="text-xl leading-7 font-bold tabular-nums">
          {formatoNumeroGiorno.format(data)}
        </span>
        <span className="pb-0.5 text-[11px] text-muted-foreground uppercase">
          {formatoMeseBreve.format(data).replace('.', '')}
        </span>
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'flex items-center gap-1.5 text-lg font-semibold tabular-nums',
              annullato && 'text-muted-foreground line-through',
            )}
          >
            <ClockIcon aria-hidden className="size-4 text-muted-foreground" />
            {formatoOra.format(data)}
          </span>
          <Badge className={COLORE_SEGNO[segno]}>{ETICHETTA_SEGNO[segno]}</Badge>
        </span>
        <span className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
          <MapPinIcon aria-hidden className="size-4 shrink-0" />
          <span className="truncate">{a.luogo ?? 'Luogo da definire'}</span>
        </span>
      </span>

      {a.stato === 'svolto' && (
        <span className="flex w-16 shrink-0 flex-col items-end gap-1">
          <span className="flex items-center gap-1 font-bold tabular-nums">
            <UsersIcon aria-hidden className="size-4 text-muted-foreground" />
            {a.presenti}/{a.rosa}
          </span>
          <Progress
            value={a.rosa > 0 ? (a.presenti / a.rosa) * 100 : 0}
            aria-label="Presenti sulla rosa"
          />
        </span>
      )}
    </Link>
  );
}
